import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertContent, CardRadio } from 'components';
import { SETTINGS_GATE_ACTIONS, SettingsGateCopy, SettingsGateOptions } from 'config/const';
import { theme, toRem } from 'design';
import { CalculatorSettingsChoice, SettingsGateState } from 'types';

/** The CHANGED state is wider to seat the two option cards side by side; the informational states are not. */
const CHANGED_MODAL_WIDTH = 700;
const INFORMATIONAL_MODAL_WIDTH = 528;

/* `AlertContent` panels sit on `gutter.xlarge` where the processing modal's sits on `large`. Since the two swap
 * inside one shell, matching it keeps the gate from rendering 22px lower than the modal it replaces. */
const GateLayout = styled(AlertContent)`
  margin: ${toRem(theme.space['3xl'])} 0;
`;

/** One radio group, so selecting either card deselects the other. */
const OPTION_GROUP_NAME = 'calculator-settings-choice';

const OptionCards = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${toRem(theme.space.lg)};
  width: 100%;

  & > * {
    flex: 1;
    min-width: ${toRem(200)};
  }
`;

export interface ISettingsGateModalProps {
  state: SettingsGateState;
  onCancel: () => void;
  /** Carries the manager's choice on the CHANGED state, and nothing on the informational states. */
  onCalculate: (choice?: CalculatorSettingsChoice) => void;
}

/**
 * Tells a manager what is known about a paid date's calculator settings before recalculating it.
 *
 * One component rather than three: all three states share the panel, the Cancel action and the
 * Calculate Payments action, and differ only in copy and whether the option cards are present.
 *
 * On the CHANGED state neither option is preselected — the manager has to make the call — so
 * Calculate Payments stays disabled until one is picked.
 *
 * Rendered without a modal shell of its own: the caller already has one mounted and swaps its
 * contents, rather than tearing that backdrop down and building another. Two shells trading places
 * overlap their fades and drop the body scroll lock for a frame, which flashes the screen.
 */
export const SettingsGateContent = ({ state, onCancel, onCalculate }: ISettingsGateModalProps) => {
  const [choice, setChoice] = useState<CalculatorSettingsChoice>();
  const requiresChoice = state === SettingsGateState.CHANGED;
  const { title, message } = SettingsGateCopy[state];

  // A caller that swaps `state` on a mounted modal must not carry a previous selection back into the
  // CHANGED state — that would re-open it with an option preselected and Calculate already enabled.
  useEffect(() => setChoice(undefined), [state]);

  return (
    <GateLayout
      dataCy="settings-gate-modal"
      maxWidth={requiresChoice ? CHANGED_MODAL_WIDTH : INFORMATIONAL_MODAL_WIDTH}
      // The design carries no alert icon in any of the three states, and centers auto-width actions
      // rather than stretching them across the shell.
      hideIcon
      centerButtons
      title={title}
      message={message}
      onClose={onCancel}
      leftActionLabel={SETTINGS_GATE_ACTIONS.cancel}
      onLeftAction={onCancel}
      rightActionLabel={SETTINGS_GATE_ACTIONS.calculate}
      onRightAction={() => onCalculate(requiresChoice ? choice : undefined)}
      disabled={requiresChoice && !choice}
    >
      {requiresChoice && (
        <OptionCards role="radiogroup" data-cy="settings-gate-options">
          {SettingsGateOptions.map((option) => (
            <CardRadio key={option.choice} name={OPTION_GROUP_NAME} title={option.title} description={option.message} checked={choice === option.choice} onChange={() => setChoice(option.choice)} dataCy={option.dataCy} />
          ))}
        </OptionCards>
      )}
    </GateLayout>
  );
};
